import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { InfoTip } from '@/components/ui/info-tip'
import { Loader2, TrendingUp, RefreshCw, AlignStartVertical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFlexBands } from '@/hooks/use-monitoring'
import { useControlSignals } from '@/hooks/use-control'
import D3FlexChart, {
  D3MinimapBrush,
  type D3FlexChartPoint,
  type D3FlexChartSeries,
} from './D3FlexChart'

type ChartView = 'interval' | 'cumulative' | 'both'

interface FlexibilityBandChartProps {
  poolID: string
  /** Whether to display interval values as average power (W) or energy (Wh). Defaults to 'power'. */
  displayMode?: 'power' | 'energy'
  /** Callback to change display mode */
  onDisplayModeChange?: (mode: 'power' | 'energy') => void
  /** Which charts to display: interval only, cumulative only, or both. Defaults to 'interval'. */
  chartView?: ChartView
  /** Callback to change chart view */
  onChartViewChange?: (view: ChartView) => void
  /**
   * Controlled baseline blend (0–100). When provided the component is in
   * controlled mode and no internal state is used for this value.
   */
  midpointMix?: number
  onMidpointMixChange?: (v: number) => void
  /**
   * Controlled normalize-cumulative flag. When provided the component is in
   * controlled mode and no internal state is used for this value.
   */
  normalizeCumulative?: boolean
  onNormalizeCumulativeChange?: (v: boolean) => void
}

const QUICK_RANGES = [4, 24, 48] as const

function determineEnergyScale(values: number[]): { divisor: number; unit: string } {
  const maxValue = Math.max(...values.filter((v) => !isNaN(v) && isFinite(v)))
  if (maxValue >= 1_000_000) return { divisor: 1_000_000, unit: 'MWh' }
  if (maxValue >= 1_000) return { divisor: 1_000, unit: 'kWh' }
  return { divisor: 1, unit: 'Wh' }
}

function determinePowerScale(values: number[]): { divisor: number; unit: string } {
  const maxValue = Math.max(...values.filter((v) => !isNaN(v) && isFinite(v)))
  if (maxValue >= 1_000_000) return { divisor: 1_000_000, unit: 'MW' }
  if (maxValue >= 1_000) return { divisor: 1_000, unit: 'kW' }
  return { divisor: 1, unit: 'W' }
}

function formatWindowDescription(brushDomain: [number, number]): string {
  const fmtTime = (ms: number) =>
    new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  const fmtDate = (ms: number) =>
    new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const windowHours = Math.round((brushDomain[1] - brushDomain[0]) / 3_600_000)
  const startDate = new Date(brushDomain[0])
  const endDate = new Date(brushDomain[1])
  const sameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()
  return sameDay
    ? `${fmtDate(brushDomain[0])}, ${fmtTime(brushDomain[0])} – ${fmtTime(brushDomain[1])} · ${windowHours}h`
    : `${fmtDate(brushDomain[0])} ${fmtTime(brushDomain[0])} – ${fmtDate(brushDomain[1])} ${fmtTime(brushDomain[1])} · ${windowHours}h`
}

interface ScheduledTask {
  powerTarget: number
  startTime: string
  endTime: string
}

/**
 * For each flex-band interval, compute the energy (Wh) implied by the
 * overlapping control tasks: energy = Σ powerTarget_k × overlap_k / 3600.
 *
 * Tasks with no overlap with a given interval contribute 0.
 * Returns an array parallel to `bands` with the control energy per interval.
 */
function controlEnergyPerBand(
  bands: Array<{ startAt: string; endAt: string }>,
  tasks: ScheduledTask[]
): number[] {
  return bands.map((band) => {
    const bStart = new Date(band.startAt).getTime()
    const bEnd = new Date(band.endAt).getTime()
    let totalWh = 0
    for (const task of tasks) {
      const tStart = new Date(task.startTime).getTime()
      const tEnd = new Date(task.endTime).getTime()
      const overlapMs = Math.max(0, Math.min(tEnd, bEnd) - Math.max(tStart, bStart))
      if (overlapMs > 0) {
        totalWh += (task.powerTarget * (overlapMs / 1000)) / 3600
      }
    }
    return totalWh
  })
}

export default function FlexibilityBandChart({
  poolID,
  displayMode = 'power',
  onDisplayModeChange,
  chartView = 'interval',
  onChartViewChange,
  midpointMix: midpointMixProp,
  onMidpointMixChange,
  normalizeCumulative: normalizeCumulativeProp,
  onNormalizeCumulativeChange,
}: FlexibilityBandChartProps) {
  const { data: allBands = [], isLoading: loading, isError, error, refetch } = useFlexBands(poolID)
  const { data: controlTasks = [] } = useControlSignals(poolID)

  const [hiddenSeriesInstant, setHiddenSeriesInstant] = useState<Record<string, boolean>>({})
  const [hiddenSeriesCumulative, setHiddenSeriesCumulative] = useState<Record<string, boolean>>({})
  const [sharedHoveredTs, setSharedHoveredTs] = useState<number | null>(null)

  // Controlled vs. uncontrolled pattern for normalizeCumulative and midpointMix
  const [normalizeCumulativeInternal, setNormalizeCumulativeInternal] = useState(false)
  const [midpointMixInternal, setMidpointMixInternal] = useState(0)

  const isControlledNormalize = normalizeCumulativeProp !== undefined
  const normalizeCumulative = isControlledNormalize
    ? normalizeCumulativeProp
    : normalizeCumulativeInternal
  const setNormalizeCumulative = (v: boolean) => {
    if (isControlledNormalize) {
      onNormalizeCumulativeChange?.(v)
    } else {
      setNormalizeCumulativeInternal(v)
    }
  }

  const isControlledMix = midpointMixProp !== undefined
  const midpointMix = isControlledMix ? midpointMixProp : midpointMixInternal
  const setMidpointMix = (v: number) => {
    if (isControlledMix) {
      onMidpointMixChange?.(v)
    } else {
      setMidpointMixInternal(v)
    }
  }

  // ── Shared brush domain ───────────────────────────────────────────────────
  const [brushDomain, setBrushDomain] = useState<[number, number] | null>(null)

  const fullDomain = useMemo<[number, number] | null>(() => {
    if (allBands.length === 0) return null
    return [
      new Date(allBands[0].startAt).getTime(),
      new Date(allBands[allBands.length - 1].endAt).getTime(),
    ]
  }, [allBands])

  // Seed brush domain to "now → now+24h" once data is available
  useEffect(() => {
    if (!fullDomain || brushDomain) return
    const now = Date.now()
    const start = Math.max(fullDomain[0], now)
    const end = Math.min(start + 24 * 3_600_000, fullDomain[1])
    setBrushDomain([start, end])
  }, [fullDomain, brushDomain])

  const windowHours = brushDomain ? Math.round((brushDomain[1] - brushDomain[0]) / 3_600_000) : 24
  const activeQuickRange = QUICK_RANGES.find((h) => h === windowHours) ?? null
  const windowDescription = useMemo(
    () => (brushDomain ? formatWindowDescription(brushDomain) : `Next ${windowHours}h`),
    [brushDomain, windowHours]
  )

  const setQuickRange = (h: number) => {
    if (!fullDomain) return
    const now = Date.now()
    const start = Math.max(fullDomain[0], now)
    setBrushDomain([start, Math.min(start + h * 3_600_000, fullDomain[1])])
  }

  const errorMessage = useMemo(() => {
    if (!isError) return null
    const msg = (error as any)?.message ?? ''
    if (msg.includes('403') || msg.includes('does not allow'))
      return 'This pool does not have flexibility bands configured'
    if (msg.includes('500') || msg.includes('no forecast'))
      return 'No forecast data available for this pool'
    return null
  }, [isError, error])

  // ── Scales — computed from ALL bands so units stay stable as brush moves ──
  const instantScale = useMemo(() => {
    if (displayMode === 'power') {
      // Convert each band's energy values to average power (W) using interval duration
      const powerValues = allBands.flatMap((b) => {
        const durationMs = new Date(b.endAt).getTime() - new Date(b.startAt).getTime()
        const durationH = durationMs / 3_600_000
        if (durationH <= 0) return []
        return [
          (b.eMin || 0) / durationH,
          (b.eMax || 0) / durationH,
          (b.eBaseline || 0) / durationH,
        ]
      })
      return determinePowerScale(powerValues)
    } else {
      return determineEnergyScale(
        allBands.flatMap((b) => [b.eMin || 0, b.eMax || 0, b.eBaseline || 0])
      )
    }
  }, [allBands, displayMode])
  const cumulativeScale = useMemo(
    () => determineEnergyScale(allBands.flatMap((b) => [b.eMinRange || 0, b.eMaxRange || 0])),
    [allBands]
  )

  // ── Control task energy per flex-band interval ────────────────────────────
  const controlEnergyValues = useMemo(
    () => controlEnergyPerBand(allBands, controlTasks as ScheduledTask[]),
    [allBands, controlTasks]
  )

  // ── Chart data arrays ─────────────────────────────────────────────────────
  // Cumulative data is computed first because instantData derives the midpoint
  // baseline from the per-interval delta of cumulativeMidpointBaseline.
  const cumulativeData = useMemo<D3FlexChartPoint[]>(() => {
    if (allBands.length === 0) return []
    const firstTs = new Date(allBands[0].startAt).getTime()
    // Sentinel point at t=0 so all linear lines start from the origin.
    // ts === endTs so it plots at the chart's left edge without contributing
    // an interval of its own.
    const origin: D3FlexChartPoint = {
      ts: firstTs,
      endTs: firstTs,
      minRange: 0,
      maxRange: 0,
      cumulativeBaseline: 0,
      cumulativeMidpointBaseline: 0,
      cumulativeCost: 0,
      cumulativeMidpointCost: 0,
    }
    let runningBaseline = 0
    let runningCost = 0
    let runningMidpointCost = 0
    const rest = allBands.map((band) => {
      runningBaseline += band.eBaseline || 0
      const minRange = (band.eMinRange || 0) / cumulativeScale.divisor
      const maxRange = (band.eMaxRange || 0) / cumulativeScale.divisor

      // Accumulate cost: eBaseline (Wh) × daPriceEurKwh (EUR/kWh) / 1000 → EUR
      const price = band.daPriceEurKwh
      let intervalCost: number | undefined
      let intervalMidpointCost: number | undefined
      if (price !== undefined) {
        intervalCost = ((band.eBaseline || 0) * price) / 1000
        runningCost += intervalCost
        // Midpoint energy for this interval (Wh) = (eMax + eMin) / 2
        const midpointEnergyWh = ((band.eMax || 0) + (band.eMin || 0)) / 2
        intervalMidpointCost = (midpointEnergyWh * price) / 1000
        runningMidpointCost += intervalMidpointCost
      }

      return {
        ts: new Date(band.startAt).getTime(),
        endTs: new Date(band.endAt).getTime(),
        minRange,
        maxRange,
        cumulativeBaseline: runningBaseline / cumulativeScale.divisor,
        cumulativeMidpointBaseline: (minRange + maxRange) / 2,
        // Cumulative cost: undefined when no price data has been seen yet
        cumulativeCost: price !== undefined || runningCost > 0 ? runningCost : undefined,
        cumulativeMidpointCost:
          price !== undefined || runningMidpointCost > 0 ? runningMidpointCost : undefined,
        // Per-interval cost (used by normalized view)
        intervalCost,
        intervalMidpointCost,
      }
    })
    return [origin, ...rest]
  }, [allBands, cumulativeScale.divisor])

  const instantData = useMemo<D3FlexChartPoint[]>(() => {
    const mix = midpointMix / 100
    return allBands.map((band, i) => {
      const startMs = new Date(band.startAt).getTime()
      const endMs = new Date(band.endAt).getTime()
      const durationH = (endMs - startMs) / 3_600_000
      const toScaled = (wh: number) => {
        if (displayMode === 'power') {
          return durationH > 0 ? wh / durationH / instantScale.divisor : 0
        }
        return (wh || 0) / instantScale.divisor
      }
      const ctrlWh = controlEnergyValues[i]
      const scaledMin = toScaled(band.eMin || 0)
      const scaledMax = toScaled(band.eMax || 0)

      // Midpoint baseline for the instant chart is derived from the cumulative
      // midpoint: delta = cumMidpoint[i+1] - cumMidpoint[i] (the origin sentinel
      // is index 0, so band i corresponds to cumulativeData[i+1]).
      // The cumulative midpoint is already in cumulativeScale units, so convert
      // back to raw Wh before applying toScaled for consistent power/energy mode.
      const cumPrev = (cumulativeData[i]?.cumulativeMidpointBaseline as number) ?? 0
      const cumCurr = (cumulativeData[i + 1]?.cumulativeMidpointBaseline as number) ?? 0
      const midpointDeltaWh = (cumCurr - cumPrev) * cumulativeScale.divisor

      const apiBaseline = toScaled(band.eBaseline || 0)
      const midpointBaseline = toScaled(midpointDeltaWh)

      return {
        ts: startMs,
        endTs: endMs,
        energyMin: scaledMin,
        energyMax: scaledMax,
        baseline: mix * midpointBaseline + (1 - mix) * apiBaseline,
        controlEnergy: ctrlWh !== 0 ? toScaled(ctrlWh) : undefined,
        // Always show raw DA price (EUR/kWh) regardless of power/energy display mode.
        daPrice: band.daPriceEurKwh,
      }
    })
  }, [
    allBands,
    instantScale.divisor,
    controlEnergyValues,
    displayMode,
    cumulativeData,
    cumulativeScale.divisor,
    midpointMix,
  ])

  // Mixed cumulative baseline: proportional blend of API and midpoint baselines.
  // Used as both the rendered series and the floor for normalization.
  const mixedCumulativeData = useMemo<D3FlexChartPoint[]>(() => {
    const mix = midpointMix / 100
    return cumulativeData.map((pt) => ({
      ...pt,
      cumulativeBaseline:
        mix * (pt.cumulativeMidpointBaseline as number) +
        (1 - mix) * (pt.cumulativeBaseline as number),
      // Blend cost: both undefined → keep undefined; otherwise blend using available values
      cumulativeCost:
        pt.cumulativeCost === undefined && pt.cumulativeMidpointCost === undefined
          ? undefined
          : mix * ((pt.cumulativeMidpointCost as number) ?? 0) +
            (1 - mix) * ((pt.cumulativeCost as number) ?? 0),
      // Blended per-interval cost (used by normalized view)
      intervalCost:
        pt.intervalCost === undefined && pt.intervalMidpointCost === undefined
          ? undefined
          : mix * ((pt.intervalMidpointCost as number) ?? 0) +
            (1 - mix) * ((pt.intervalCost as number) ?? 0),
    }))
  }, [cumulativeData, midpointMix])

  // When normalization is on, subtract each point's mixed baseline from minRange
  // and maxRange so the active baseline is pinned to zero.
  // Also swap cumulativeCost → per-interval cost so the right axis shows the
  // cost of each interval rather than a cumulative sum against a shifted floor.
  const normalizedCumulativeData = useMemo<D3FlexChartPoint[]>(() => {
    if (!normalizeCumulative || mixedCumulativeData.length === 0) return mixedCumulativeData
    return mixedCumulativeData.map((pt) => {
      const floor = (pt.cumulativeBaseline as number) ?? 0
      const normMin = (pt.minRange as number) - floor
      const normMax = (pt.maxRange as number) - floor
      return {
        ...pt,
        minRange: normMin,
        maxRange: normMax,
        cumulativeBaseline: 0,
        cumulativeMidpointBaseline: (normMin + normMax) / 2,
        // Replace cumulative cost with per-interval cost when normalized
        cumulativeCost: pt.intervalCost,
      }
    })
  }, [mixedCumulativeData, normalizeCumulative])

  // ── Series definitions ────────────────────────────────────────────────────
  // Color blends from indigo (#6366f1) at mix=0 to purple (#a855f7) at mix=100
  const baselineColor = useMemo(() => {
    const mix = midpointMix / 100
    const r = Math.round(0x63 + mix * (0xa8 - 0x63))
    const g = Math.round(0x66 + mix * (0x55 - 0x66))
    const b = Math.round(0xf1 + mix * (0xf7 - 0xf1))
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }, [midpointMix])

  const baselineName = useMemo(() => {
    if (midpointMix === 0) return 'API Baseline'
    if (midpointMix === 100) return 'Midpoint Baseline'
    return `Baseline (${midpointMix}% midpoint)`
  }, [midpointMix])

  const instantSeries = useMemo<D3FlexChartSeries[]>(
    () => [
      {
        key: 'flexBand',
        name: `Flexibility Band [${instantScale.unit}]`,
        color: '#10b981',
        type: 'band',
        bandKeys: ['energyMin', 'energyMax'] as [string, string],
        fillOpacity: 0.25,
        hidden: hiddenSeriesInstant.flexBand,
      },
      {
        key: 'baseline',
        name: `${baselineName} [${instantScale.unit}]`,
        color: baselineColor,
        type: 'line',
        hidden: hiddenSeriesInstant.baseline,
      },
      {
        key: 'controlEnergy',
        name: `Control Target [${instantScale.unit}]`,
        color: '#ef4444',
        type: 'line',
        lineWidth: 3,
        hidden: hiddenSeriesInstant.controlEnergy,
      },
      {
        key: 'daPrice',
        name: 'DA Price [EUR/kWh]',
        color: '#f59e0b',
        type: 'line',
        lineWidth: 1.5,
        yAxis: 'right',
        hidden: hiddenSeriesInstant.daPrice,
      },
    ],
    [instantScale.unit, hiddenSeriesInstant, baselineName, baselineColor]
  )

  const cumulativeSeries = useMemo<D3FlexChartSeries[]>(
    () => [
      {
        key: 'maxRange',
        name: `Max Cumulative Energy [${cumulativeScale.unit}]`,
        color: '#3b82f6',
        type: 'area',
        fillOpacity: 0.2,
        interpolation: normalizeCumulative ? 'step-after' : 'linear',
        tooltipMode: 'delta',
        hidden: hiddenSeriesCumulative.maxRange,
      },
      {
        key: 'minRange',
        name: `Min Cumulative Energy [${cumulativeScale.unit}]`,
        color: '#f59e0b',
        type: 'area',
        fillOpacity: 0.2,
        interpolation: normalizeCumulative ? 'step-after' : 'linear',
        tooltipMode: 'delta',
        hidden: hiddenSeriesCumulative.minRange,
      },
      {
        key: 'cumulativeBaseline',
        name: `${baselineName} [${cumulativeScale.unit}]`,
        color: baselineColor,
        type: 'line',
        interpolation: 'linear',
        tooltipMode: 'delta',
        hidden: hiddenSeriesCumulative.cumulativeBaseline,
      },
      {
        key: 'cumulativeCost',
        name: normalizeCumulative
          ? `${baselineName} Interval Cost [EUR]`
          : `${baselineName} Cumulative Cost [EUR]`,
        color: '#f97316',
        type: 'line',
        lineWidth: 1.5,
        interpolation: normalizeCumulative ? 'step-after' : 'linear',
        tooltipMode: 'delta',
        yAxis: 'right',
        hidden: hiddenSeriesCumulative.cumulativeCost,
      },
    ],
    [cumulativeScale.unit, hiddenSeriesCumulative, baselineName, baselineColor, normalizeCumulative]
  )

  const toggleInstant = (key: string) =>
    setHiddenSeriesInstant((prev) => ({ ...prev, [key]: !prev[key] }))
  const toggleCumulative = (key: string) =>
    setHiddenSeriesCumulative((prev) => ({ ...prev, [key]: !prev[key] }))

  // ── Shared slider row ─────────────────────────────────────────────────────
  const sliderRow = (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">API</span>
      <Slider
        min={0}
        max={100}
        step={1}
        value={[midpointMix]}
        onValueChange={([v]) => setMidpointMix(v)}
        className="w-24"
      />
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">Midpoint</span>
      <InfoTip
        content={
          <>
            <p className="font-medium mb-1">Baseline blend</p>
            <p>Blends two baseline definitions for the cumulative and instant charts:</p>
            <ul className="mt-1 space-y-0.5">
              <li>
                <strong>API baseline</strong> — the forecaster's expected consumption without any
                dispatch command.
              </li>
              <li>
                <strong>Midpoint baseline</strong> — midpoint between min and max cumulative energy
                at each point in time, derived purely from the flexibility band boundaries.
              </li>
            </ul>
            <p className="mt-1 text-muted-foreground">
              Drag fully left for 100% API, fully right for 100% midpoint, or anywhere in between
              for a proportional mix.
            </p>
          </>
        }
      />
    </div>
  )

  const instantTitle =
    displayMode === 'power' ? 'Average Power Per Interval' : 'Energy Per Interval'
  const cumulativeTitle = 'Cumulative Energy'

  const loadingOrEmpty =
    loading && allBands.length === 0 ? (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    ) : allBands.length === 0 ? (
      <div className="text-center py-12 text-muted-foreground">
        <p>No flexibility bands available</p>
        <p className="text-sm mt-2">
          {errorMessage || 'This pool may not have forecast data configured'}
        </p>
      </div>
    ) : null

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Flexibility Band
            </CardTitle>
            <CardDescription>{windowDescription}</CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Chart view selector */}
            {onChartViewChange && (
              <div className="flex rounded-md border bg-muted/40 p-0.5">
                <button
                  onClick={() => onChartViewChange('interval')}
                  className={cn(
                    'px-2 py-1 rounded-sm text-xs font-medium transition-all whitespace-nowrap',
                    chartView === 'interval'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Interval
                </button>
                <button
                  onClick={() => onChartViewChange('cumulative')}
                  className={cn(
                    'px-2 py-1 rounded-sm text-xs font-medium transition-all whitespace-nowrap',
                    chartView === 'cumulative'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Cumulative
                </button>
                <button
                  onClick={() => onChartViewChange('both')}
                  className={cn(
                    'px-2 py-1 rounded-sm text-xs font-medium transition-all whitespace-nowrap',
                    chartView === 'both'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Both
                </button>
              </div>
            )}
            {/* Quick-range buttons */}
            <div className="flex items-center gap-1.5">
              <InfoTip
                content={
                  <>
                    <p className="font-medium mb-1">Quick time range</p>
                    <p>
                      Shifts the visible window to start from now and extend forward by the selected
                      number of hours. The minimap below shows where this window sits within the
                      full 48-hour forecast.
                    </p>
                  </>
                }
              />
              <div className="flex gap-1">
                {QUICK_RANGES.map((h) => (
                  <Button
                    key={h}
                    variant={activeQuickRange === h ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setQuickRange(h)}
                  >
                    {h}h
                  </Button>
                ))}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {loadingOrEmpty ?? (
          <div className="flex flex-col h-full gap-3">
            {/* Slider row — shared between both charts */}
            <div className="flex items-center gap-1.5 shrink-0">{sliderRow}</div>

            {/* Per-interval chart */}
            {(chartView === 'interval' || chartView === 'both') && (
              <div className="flex-1 flex flex-col min-h-0 border rounded-md overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 shrink-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium">{instantTitle}</h3>
                    <InfoTip
                      content={
                        <>
                          <p className="font-medium mb-1">
                            {displayMode === 'power'
                              ? 'Average power per interval'
                              : 'Energy per interval'}
                          </p>
                          <p>
                            {displayMode === 'power'
                              ? 'Each step shows the average power envelope for one 15-minute settlement window, derived from the interval energy values (power = energy ÷ duration):'
                              : 'Each step shows the energy envelope for one 15-minute settlement window:'}
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            <li>
                              <strong className="text-emerald-500">Flexibility Band</strong> —
                              shaded area between min and max: the range the pool is allowed to
                              consume.
                            </li>
                            <li>
                              <strong className="text-indigo-400">Baseline</strong> —{' '}
                              {displayMode === 'power'
                                ? 'expected average power'
                                : 'expected energy consumption'}{' '}
                              if no dispatch command is sent. Use the baseline slider to blend
                              between the API baseline and the midpoint between min and max.
                            </li>
                            <li>
                              <strong className="text-red-500">Control Target</strong> —{' '}
                              {displayMode === 'power' ? 'average power' : 'energy'} implied by
                              active dispatch tasks overlapping this interval (only shown when a
                              task exists).
                            </li>
                          </ul>
                          <p className="mt-1 text-muted-foreground">
                            Click a series chip to toggle its visibility. Drag on the chart to zoom.
                          </p>
                        </>
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{instantScale.unit}</span>
                    {onDisplayModeChange && (
                      <div className="flex rounded-md border bg-muted/40 p-0.5">
                        <button
                          onClick={() => onDisplayModeChange('power')}
                          className={cn(
                            'px-2 py-0.5 rounded-sm text-[10px] font-medium transition-all',
                            displayMode === 'power'
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                        >
                          Power
                        </button>
                        <button
                          onClick={() => onDisplayModeChange('energy')}
                          className={cn(
                            'px-2 py-0.5 rounded-sm text-[10px] font-medium transition-all',
                            displayMode === 'energy'
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                        >
                          Energy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <D3FlexChart
                    data={instantData}
                    series={instantSeries}
                    unit={instantScale.unit}
                    rightUnit="EUR/kWh"
                    xDomain={brushDomain ?? undefined}
                    fillContainer
                    onSeriesToggle={toggleInstant}
                    hoveredTs={sharedHoveredTs}
                    onHoverChange={setSharedHoveredTs}
                    onRangeSelect={(start, end) => setBrushDomain([start, end])}
                  />
                </div>
              </div>
            )}

            {/* Cumulative chart */}
            {(chartView === 'cumulative' || chartView === 'both') && (
              <div className="flex-1 flex flex-col min-h-0 border rounded-md overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 shrink-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium">{cumulativeTitle}</h3>
                    <InfoTip
                      content={
                        <>
                          <p className="font-medium mb-1">Cumulative energy</p>
                          <p>
                            Running totals from the start of the forecast horizon to the end of each
                            interval:
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            <li>
                              <strong className="text-blue-400">Max Cumulative</strong> — upper
                              bound on energy that can be added to the pool up to this point.
                            </li>
                            <li>
                              <strong className="text-amber-400">Min Cumulative</strong> — lower
                              bound on energy that can be withdrawn or must be added to the pool up
                              to this point.
                            </li>
                            <li>
                              <strong className="text-indigo-400">Baseline</strong> — Cumulative
                              energy consumption of the pool, following the baseline. (blend of the
                              API baseline and the midpoint between min and max, controlled by the
                              slider. Fully left = 100% API; fully right = 100% midpoint.)
                            </li>
                            <li>
                              <strong className="text-orange-500">Baseline Cost</strong> — Running
                              cost of the baseline energy consumption in EUR (right axis), computed
                              as cumulative sum of baseline energy × DA price per interval. Only
                              shown when DA price data is available.
                            </li>
                          </ul>
                          <p className="mt-1 text-muted-foreground">
                            Hover tooltips show the delta from the previous interval (Δ). Click
                            series chips to toggle.
                          </p>
                        </>
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{cumulativeScale.unit}</span>
                    <Button
                      variant={normalizeCumulative ? 'default' : 'outline'}
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setNormalizeCumulative(!normalizeCumulative)}
                    >
                      <AlignStartVertical className="h-3 w-3 mr-1" />
                      Normalize
                    </Button>
                    <InfoTip
                      content={
                        <>
                          <p className="font-medium mb-1">Normalize</p>
                          <p>
                            Subtracts each point's own baseline from all values, pinning the
                            baseline to zero at every moment. Min and max are then shown as
                            deviation below and above expected consumption, making the flexibility
                            headroom immediately visible.
                          </p>
                          <p className="mt-1 text-muted-foreground">
                            Uses the currently blended baseline as the reference. Combine with the
                            blend slider to normalize against the API baseline, the midpoint, or any
                            mix of the two.
                          </p>
                        </>
                      }
                    />
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <D3FlexChart
                    data={normalizedCumulativeData}
                    tooltipData={normalizeCumulative ? mixedCumulativeData : undefined}
                    series={cumulativeSeries}
                    unit={cumulativeScale.unit}
                    rightUnit="EUR"
                    xDomain={brushDomain ?? undefined}
                    fillContainer
                    onSeriesToggle={toggleCumulative}
                    hoveredTs={sharedHoveredTs}
                    onHoverChange={setSharedHoveredTs}
                    onRangeSelect={(start, end) => setBrushDomain([start, end])}
                  />
                </div>
              </div>
            )}

            {/* Minimap — shared between both charts */}
            {fullDomain && brushDomain && (
              <div className="shrink-0">
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">
                  Overview — drag to pan or resize window
                  <InfoTip
                    side="left"
                    content={
                      <>
                        <p className="font-medium mb-1">Minimap overview</p>
                        <p>
                          Shows the full 48-hour forecast. The highlighted region is the window
                          currently displayed in both charts above.
                        </p>
                        <p className="mt-1">
                          <strong>Drag</strong> the highlighted region to pan. <strong>Drag</strong>{' '}
                          either edge handle to extend or shrink the window. The quick-range buttons
                          reset the window to a fixed duration from now.
                        </p>
                      </>
                    }
                  />
                </h4>
                <D3MinimapBrush
                  data={instantData}
                  series={instantSeries}
                  brushDomain={brushDomain}
                  onBrushChange={(start, end) => setBrushDomain([start, end])}
                  height={72}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
